import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useTypeSelector } from '../../store';
import { selectFaqList } from '../../selectors/faq';
import { deleteFaq, getFaqList } from '../../actions/faq';
import { units } from '../../helpers/styles/units';
import { CreateFaq } from './CreateFaq';
import { EditFaq } from './EditFaq';
import { FaqTablePage } from './FaqTablePage';
import { FaqFilterKey, PageStatus } from './types';
import type { FaqItem, FaqParentProps } from './types';

const StyledRootWrapper = styled.div`
  position: relative;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;

  padding-left: ${units(12)};
  margin-bottom: ${units(63)};
`;

const StyledContent = styled.div`
  margin-top: ${units(12)};
  max-width: ${units(700)};
`;

const StyledTitle = styled.h2`
  margin: 0 0 ${units(8)} 0;
`;

const StyledSubTitle = styled.div`
  margin-bottom: ${units(12)};
`;

export type FaqSectionProps = {
    title: string;
    subtitle?: string;
    entityId?: number;
    filterKey: FaqFilterKey;
};

export const FaqSectionContent = ({
    title,
    subtitle = '',
    entityId,
    filterKey,
}: FaqSectionProps) => {
    const dispatch = useAppDispatch();
    const list = useTypeSelector(selectFaqList);
    const [activeStatus, setActiveStatus] = useState<PageStatus>(PageStatus.MAIN);
    const [selectedFaq, setSelectedFaq] = useState<FaqItem | null>(null);

    const parentProps = useMemo<FaqParentProps>(
        () => (filterKey === 'categoryId'
            ? { categoryId: entityId }
            : { oftenSearchedId: entityId }),
        [entityId, filterKey],
    );

    const listParams = useMemo(
        () => ({
            page: 1,
            size: 100,
            ...(entityId ? { [filterKey]: entityId } : {}),
        }),
        [entityId, filterKey],
    );

    useEffect(() => {
        if (entityId) {
            dispatch(getFaqList(listParams));
        }
    }, [dispatch, entityId, listParams]);

    const handleDelete = (id: number) => {
        dispatch(deleteFaq(id));
    };

    const handleSaved = () => {
        setActiveStatus(PageStatus.MAIN);
        setSelectedFaq(null);
    };

    const getPageContent = () => {
        if (activeStatus === PageStatus.MAIN) {
            return (
                <FaqTablePage
                    list={list}
                    onDelete={handleDelete}
                    changePage={(status, data = null) => {
                        setActiveStatus(status);
                        if (data) {
                            setSelectedFaq(data);
                        }
                    }}
                />
            );
        }

        if (activeStatus === PageStatus.ADD) {
            return (
                <CreateFaq
                    {...parentProps}
                    changePage={status => setActiveStatus(status)}
                    onSaved={handleSaved}
                />
            );
        }

        if (activeStatus === PageStatus.EDIT && selectedFaq) {
            return (
                <EditFaq
                    data={selectedFaq}
                    {...parentProps}
                    changePage={(status, data = null) => {
                        setActiveStatus(status);
                        setSelectedFaq(data);
                    }}
                    onSaved={handleSaved}
                />
            );
        }

        return null;
    };

    const contentNode = getPageContent();

    return (
        <StyledRootWrapper>
            <StyledWrapper>
                <StyledTitle>{title}</StyledTitle>
                <StyledSubTitle>{subtitle}</StyledSubTitle>
                <StyledContent>{contentNode}</StyledContent>
            </StyledWrapper>
        </StyledRootWrapper>
    );
};

export type { FaqSectionProps };
